/**
 * Telling a task that is stuck from one that is merely long. Nothing here limits how many steps a
 * task takes or how long it runs: a task that keeps getting somewhere may go on as long as it
 * needs. What is seen is a task that stopped getting anywhere, which is a sequence of tool calls
 * that changes nothing: the same call with the same result again and again, or a few calls taken
 * in turn that each give the same result every time.
 *
 * Only digests, counts and tool names are kept. A call is its tool and its arguments, a result is
 * what came back, and both are reduced to a digest by the caller before they get here; neither
 * the arguments nor the output are ever held, so nothing here can leak a secret.
 */

/** One finished tool call, as the detector sees it. */
export interface ToolCallRecord {
  /** Tool name only; it names the tools in a loop and nothing else. */
  name: string;
  /** Digest of the tool and its normalised arguments. */
  call: string;
  /** Digest of what the call returned or how it failed. */
  result: string;
  failed: boolean;
  /** Whether the call changed something outside the run: it recorded an effect. */
  mutating: boolean;
}

/**
 * The same call giving the same result this many times in a row. Waiting for something is a
 * legitimate reason to ask the same question and hear the same answer, a build or an upload for
 * instance, and so is a long edit that checks the same file between steps, so this is set well
 * above what a patient wait needs, while still far below anything that would look like progress.
 */
export const LOOP_IDENTICAL_RESULT = 20;

/**
 * The same call failing the same way this many times in a row. Asking again and getting the same
 * error is not waiting for anything, so it counts sooner than a repeated answer does, but a few
 * failures are an ordinary part of working something out.
 */
export const LOOP_IDENTICAL_FAILING = 6;

/**
 * A call that changes something, made again in the same way with the same result, this many
 * times in a row. It is not waiting for anything: it is doing the same thing again, and each time
 * it is done it costs what it costs, a message sent or an amount spent.
 */
export const LOOP_IDENTICAL_MUTATING = 3;

/**
 * A short cycle of calls (two or three in turn) seen this many full rounds, each call giving the
 * same result in every round. A few rounds can be a check on two or three jobs that are all still
 * running; five rounds, ten calls or more, with nothing changing in any of them, cannot.
 */
export const LOOP_CYCLE_ROUNDS = 5;
const LOOP_CYCLE_PERIODS = [2, 3] as const;

/**
 * Recent calls kept: as many as the longest rule needs, so memory stays fixed however long a task
 * runs and no rule is left with fewer calls in view than it asks for.
 */
export const LOOP_WINDOW = Math.max(
  LOOP_IDENTICAL_RESULT,
  LOOP_IDENTICAL_FAILING,
  LOOP_IDENTICAL_MUTATING,
  LOOP_CYCLE_ROUNDS * Math.max(...LOOP_CYCLE_PERIODS),
);

/**
 * A loop seen again within this many chunks of work after the last reaction counts as the same
 * loop and is answered more firmly. Later than that it is a new one.
 */
export const LOOP_CORRECTION_CHUNKS = 2;

/** Times a task is started again from scratch because of loops, over its whole run. */
export const LOOP_MAX_RESTARTS = 2;

export interface ToolLoop {
  kind: "repeat" | "cycle";
  /** Calls in one round: 1 for a repeat. */
  period: number;
  /** Calls that make up the loop. */
  length: number;
  /** Names of the tools in the loop, in order, each once. */
  tools: string[];
}

/** The most recent calls, which is all the detector looks at. */
export function trimToolLoopWindow(history: readonly ToolCallRecord[]): ToolCallRecord[] {
  return history.slice(-LOOP_WINDOW);
}

const sameStep = (a: ToolCallRecord, b: ToolCallRecord) =>
  a.call === b.call && a.result === b.result && a.failed === b.failed;

/**
 * Whether the history ends in a loop. A pure function of the history: no clock, no counts of
 * steps in the task, nothing about its length.
 */
export function detectToolLoop(history: readonly ToolCallRecord[]): ToolLoop | undefined {
  const last = history.at(-1);
  if (!last) return undefined;

  let run = 1;
  while (
    run < history.length &&
    sameStep(history[history.length - 1 - run] as ToolCallRecord, last)
  ) {
    run += 1;
  }
  const limit = last.failed
    ? LOOP_IDENTICAL_FAILING
    : last.mutating
      ? LOOP_IDENTICAL_MUTATING
      : LOOP_IDENTICAL_RESULT;
  if (run >= limit) {
    return { kind: "repeat", period: 1, length: run, tools: [last.name] };
  }

  for (const period of LOOP_CYCLE_PERIODS) {
    const length = period * LOOP_CYCLE_ROUNDS;
    if (history.length < length) continue;
    const tail = history.slice(-length);
    let cycles = true;
    for (let index = period; index < tail.length; index += 1) {
      if (!sameStep(tail[index] as ToolCallRecord, tail[index - period] as ToolCallRecord)) {
        cycles = false;
        break;
      }
    }
    // A block of one call over and over is a repeat, and has its own, higher, limit.
    const block = tail.slice(0, period);
    if (!cycles || block.every((step) => sameStep(step, block[0] as ToolCallRecord))) continue;
    return { kind: "cycle", period, length, tools: [...new Set(block.map((step) => step.name))] };
  }
  return undefined;
}

/** Where a task stands on the way from a correction to giving up, kept across pauses. */
export interface ToolLoopLadder {
  /** 1 once a reaction is outstanding; 0 before any, or once it has been left behind. */
  level: 0 | 1;
  /** Fresh starts used so far. Never given back. */
  restarts: number;
  /** The chunk of work the last reaction was made in. */
  lastChunk: number | null;
}

export const NEW_TOOL_LOOP_LADDER: ToolLoopLadder = { level: 0, restarts: 0, lastChunk: null };

export type ToolLoopReaction = "correct" | "restart" | "conclude";

/**
 * The reaction to a loop just seen in the given chunk of work. First a correction: tell the model
 * it is repeating itself. If the loop comes back soon, start the task again with a note about
 * what looped, at most `LOOP_MAX_RESTARTS` times. If it still comes back, stop with a message
 * to the person instead of going round again.
 */
export function nextToolLoopReaction(
  ladder: ToolLoopLadder,
  chunk: number,
): { reaction: ToolLoopReaction; ladder: ToolLoopLadder } {
  // A chunk number below the last reaction's cannot say how far apart two loops are, so it is not
  // taken for "soon after": the loop is met as a new one, and the restarts used stay used.
  const gap = ladder.lastChunk === null ? null : chunk - ladder.lastChunk;
  const comesBack = ladder.level === 1 && gap !== null && gap >= 0 && gap <= LOOP_CORRECTION_CHUNKS;
  if (!comesBack) {
    return { reaction: "correct", ladder: { ...ladder, level: 1, lastChunk: chunk } };
  }
  if (ladder.restarts < LOOP_MAX_RESTARTS) {
    return {
      reaction: "restart",
      ladder: { level: 1, restarts: ladder.restarts + 1, lastChunk: chunk },
    };
  }
  return { reaction: "conclude", ladder: { ...ladder, level: 1, lastChunk: chunk } };
}
